import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { Stack, useLocalSearchParams } from "expo-router";
import { useMemo } from "react";
import { View } from "react-native";
import { Badge } from "~/components/feedback/Badge";
import { DeleteMedicalButton } from "~/components/form/health-profile/DeleteMedicalButton";
import { HeaderButton } from "~/components/HeaderButton";
import { DataList } from "~/components/layout/DataList";
import { DataListItem } from "~/components/layout/DataListItem";
import { BasicGallery } from "~/components/layout/gallery/BasicGallery";
import { Dosage } from "~/components/layout/health-profile/Dosage";
import { Schedule } from "~/components/layout/health-profile/Schedule";
import { Page } from "~/components/layout/Page";
import { Subtitle, Title } from "~/components/layout/text";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Dismiss } from "~/components/permission/Dismiss";
import { Only } from "~/components/permission/Only";
import { Flex, Gap, Items, Padding } from "~/constants/layout";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";

const type = MedicalType.medication;

export default function MedicationDetails() {
    const { id }: { id: string } = useLocalSearchParams();
    const { findMedical } = useMedical();
    const { findOption } = useOptions();
    const medication = useMemo(() => findMedical(type, id), [id, findMedical]);

    if (!medication) {
        return <Dismiss />;
    }

    const option = findOption(OptionType.medications, medication.option_id);

    return (
        <Authorize ability="view" section="medications">
            <Stack.Screen
                options={{
                    title: "Medication details",
                    headerRight: () => (
                        <Can ability="update" section="medications">
                            <HeaderButton
                                title="Edit"
                                href={{ pathname: "/health-profile/medications/[id]/edit", params: { id } }}
                            />
                        </Can>
                    ),
                }}
            />
            <Page spacer>
                <View style={[Padding.top_md, Gap.lg, Flex.flex]}>
                    <View style={[Items.start, Gap.xs]}>
                        {medication.prescribed ? <Badge title="Prescribed" variant="info" size="sm" /> : null}
                        <Only when={option}>
                            <Title size="xl">{option?.title}</Title>
                        </Only>
                        <Subtitle>{medication.current ? "Current medication" : "Past medication"}</Subtitle>
                    </View>
                    <DataList>
                        <Dosage dosage={medication.dosage} title="Medication form" />
                        <Schedule schedule={medication.schedule} />
                        <DataListItem title="Description" data={medication.description} />
                    </DataList>
                    <BasicGallery images={medication.photograph_ids} />
                </View>
                <DeleteMedicalButton type={type} id={id} />
            </Page>
        </Authorize>
    );
}
